import { createAsyncThunk } from "@reduxjs/toolkit";
import { toCentimetres, toKilograms } from "../../services/bodyMeasurements";
import { baseUrl, headersDefault } from "./index";
import { getUserInfo, resetUserInfoThrottle } from "./userActions";
import { resolveAvatarImage } from "../../services/functions";

/**
 * Saves DATI PERSONALI (Profile Settings)
 * - No autosave: only called from SALVA button.
 * - Uses same endpoints used in InitialConfiguration flow.
 */
export const saveProfileSettings = createAsyncThunk(
  "profileSettings/saveProfileSettings",
  async (args = {}, { getState, dispatch, rejectWithValue }) => {
    const t = (v) => {
      const s = String(v ?? "").trim();
      return s ? s : "";
    };

    const { skipTelephone = false } = args;

    const state = getState();
    const form = state?.profileSettings?.form || {};
    const me = state?.user?.user || {};
    const myUserId = me?.id ?? 1;

    try {
      // ✅ if skipTelephone: send the currently saved phone (me.telephone)
      // so backend won't validate/store the new phone before OTP.
      const telephoneToSend = skipTelephone
        ? me.telephone ?? form.telephone ?? ""
        : form.telephone ?? "";

           const bodyUserObj = {
         name: t(form.name),
         surname: t(form.surname),
         email: t(form.email),
         telephone: t(telephoneToSend),
         ...(t(form.nickname) ? { nickname: t(form.nickname) } : {}),
       };
       const currentAvatar = String(me?.avatar ?? "").trim();
       if (currentAvatar && !/avatardefault/i.test(currentAvatar)) {
         const looksBundled =
           /^\/(static\/media|assets\/)/i.test(currentAvatar) ||
           currentAvatar.includes("static/media/") ||
           currentAvatar.includes("assets/avatars/");

         if (!looksBundled) {
           bodyUserObj.avatar = currentAvatar;

           // optional: keep parity with PublicProfileStep (avatar_url when possible)
           const storagePath = String(me?.storage_path ?? "").trim();
           const isUrl = /^https?:\/\//i.test(currentAvatar);
           const resolved = resolveAvatarImage(currentAvatar);
           const isDefaultAvatar = !!resolved && resolved !== currentAvatar;

           if (isUrl) bodyUserObj.avatar_url = currentAvatar;
           else if (!isDefaultAvatar && storagePath) {
             bodyUserObj.avatar_url = `${storagePath}${currentAvatar}`;
           }
         }
       }
 
       const bodyUser = JSON.stringify(bodyUserObj);
 


      const res1 = await fetch(`${baseUrl}/api/profilo-utenti`, {
        method: "POST",
        headers: headersDefault(),
        body: bodyUser,
      });

      const data1 = await res1.json().catch(() => null);

      if (!res1.ok) {
        return rejectWithValue(
          data1?.message ?? "Errore aggiornamento profilo"
        );
      }

      // 2) Update master club subscriber (unchanged)
      const payloadSub = JSON.stringify({
        is_public_profile: !!form.is_public_profile,
        sex: form.sex || null,
        birth_date: form.birth_date || null,
        // Not Number(): Number("80,5") is NaN, and Number("1,75") likewise,
        // so a comma silently became a null or an invalid body. These read
        // both separators and put height into whole centimetres, which is what
        // the column holds.
        initial_weight:
          form.initial_weight === "" || form.initial_weight === null
            ? null
            : toKilograms(form.initial_weight),
        height:
          form.height === "" || form.height === null
            ? null
            : toCentimetres(form.height),
        country_id: form.country_id ?? null,
        province_id: form.province_id ?? null,
        city_id: form.city_id ?? null,
        ...(form.nickname ? { nickname: form.nickname } : {}),
        extras: {
          ...(form.extras || {}),
          hobbies: Array.isArray(form.extras?.hobbies)
            ? form.extras.hobbies
            : [],
        },
      });

      const res2 = await fetch(
        `${baseUrl}/api/v1/master-club-subscribers/${myUserId}`,
        {
          method: "PATCH",
          headers: headersDefault(),
          body: payloadSub,
        }
      );

      const data2 = await res2.json().catch(() => null);

      if (!res2.ok) {
        return rejectWithValue(
          data2?.message ?? "Errore aggiornamento impostazioni"
        );
      }

      resetUserInfoThrottle(); // profile just changed — force fresh fetch
      dispatch(getUserInfo());
      return { user: data1, subscriber: data2 };
    } catch (e) {
      return rejectWithValue(e?.message ?? "Errore rete");
    }
  }
);
